/**
 * Floating action bar that sits above a text selection in the draft editor.
 *
 * Owns only the floating behaviour — portalling, positioning above the
 * selection, and keeping itself inside the viewport. Callers supply the
 * actions, so adding a second one is an array entry rather than a rewrite.
 */

import { useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { LucideIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'

/** Gap kept between the toolbar and the viewport edge. */
const EDGE_GAP = 8

export interface SelectionToolbarAction {
  id: string
  label: string
  icon: LucideIcon
  onSelect: () => void
  disabled?: boolean
}

interface SelectionToolbarProps {
  /** Viewport coords of the point the toolbar should sit above — normally the
   *  horizontal centre of the selection's top edge. */
  pos: { x: number; y: number }
  actions: SelectionToolbarAction[]
}

export function SelectionToolbar({ pos, actions }: SelectionToolbarProps) {
  const ref = useRef<HTMLDivElement | null>(null)
  const [size, setSize] = useState({ width: 0, height: 0 })

  // Measure rather than assume: the toolbar's width depends on how many
  // actions it was given, and the clamping below is wrong without it. A layout
  // effect lands the corrected position before the browser paints.
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const { offsetWidth, offsetHeight } = el
    setSize((prev) =>
      prev.width === offsetWidth && prev.height === offsetHeight
        ? prev
        : { width: offsetWidth, height: offsetHeight },
    )
  }, [actions, pos])

  if (actions.length === 0) return null

  // Centred via translate(-50%, -100%), so clamp the centre x to keep the full
  // width on-screen, and the y so the toolbar never clips off the top.
  const halfWidth = size.width / 2
  const left = Math.max(
    EDGE_GAP + halfWidth,
    Math.min(pos.x, window.innerWidth - halfWidth - EDGE_GAP),
  )
  const top = Math.max(size.height + EDGE_GAP, pos.y)

  return createPortal(
    <div
      ref={ref}
      role="toolbar"
      aria-label="Selection actions"
      style={{
        position: 'fixed',
        left,
        top,
        transform: 'translate(-50%, -100%)',
        zIndex: 50,
      }}
      className="flex items-center gap-0.5 rounded-lg border border-border bg-popover p-1 shadow-md"
    >
      {actions.map(({ id, label, icon: Icon, onSelect, disabled }) => (
        <Button
          key={id}
          type="button"
          variant="ghost"
          size="sm"
          disabled={disabled ?? false}
          className="gap-1.5 px-2"
          onMouseDown={(e) => {
            // Keep the text selection alive — every action here reads it.
            e.preventDefault()
            if (disabled) return
            onSelect()
          }}
        >
          <Icon className="h-4 w-4 text-muted-foreground" />
          {label}
        </Button>
      ))}
    </div>,
    document.body,
  )
}
